import React, { Fragment } from 'react'
import {
   BreadcrumbItem,
   mergeClasses,
   BreadcrumbButton,
   BreadcrumbDivider,
   tokens,
   Breadcrumb,
   Divider,
   shorthands,
   makeStyles,
} from '@fluentui/react-components'
import { useNavigate, NavigateFunction } from 'react-router-dom'
import { IBreadcrumbInfo } from '../../../interfaces/IBreadcrumbInfo'
import { Title } from '../text/Title'
import { ChevronLeft24Regular } from '@fluentui/react-icons'
const useStyles = makeStyles({
   breadCrumbHeader: {
      display: 'block',
      zIndex: 3,
      position: 'sticky',
      top: '0',
      width: '100%',
      backgroundColor: tokens.colorNeutralBackground1,
      boxSizing: 'border-box',
      boxShadow: tokens.shadow8,
      ...shorthands.padding(0),
      '@media screen and (max-width: 1007px)': {
         boxShadow: 'none',
         backgroundColor: tokens.colorNeutralBackground4,
      },
   },
   breadCrumbWrapper: {
      ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalXXL),
      '@media screen and (max-width: 1007px)': {
         ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalS, 0, tokens.spacingHorizontalS),
      },
   },
   breadCrumbWrapperPadding: {
      paddingLeft: tokens.spacingHorizontalL,
      paddingRight: tokens.spacingHorizontalL,
   },
   hideInPhone: {
      display: 'flex',
      '@media screen and (max-width: 1007px)': {
         display: 'none',
      },
   },
   showOnlyPhone: {
      display: 'none',
      '@media screen and (max-width: 1007px)': {
         display: 'block',
      },
   },
   showOnlyPhoneInline: {
      display: 'none',
      '@media screen and (max-width: 1007px)': {
         display: 'inline',
      },
   },
   width40: {
      '@media screen and (max-width: 800px)': {
         width: '40px',
      },
   },
   calcWidth: {
      height: 'auto',
      '@media screen and (max-width: 1007px)': {
         width: 'calc(100% - (2 * 40px))',
         textAlign: 'center',
      },
   },
   padding: {
      ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
   },
})
type BreadCrumbHeaderProps = {
   breadCrumbItems: IBreadcrumbInfo[]
   title: string
}
export const BreadCrumbHeader = ({ breadCrumbItems, title }: BreadCrumbHeaderProps) => {
   const classes = useStyles()
   const navigate = useNavigate()

   const generateBreadcrumbs = (items: IBreadcrumbInfo[], navigate: NavigateFunction): JSX.Element[] => {
      return items.map((item, index) => {
         return (
            <Fragment key={item.title}>
               {items.length > 1 && index === items.findIndex((x) => x.active) - 1 && (
                  <BreadcrumbItem key={`${item.title}-path`} className={mergeClasses(classes.showOnlyPhone, classes.width40)} size="large">
                     <BreadcrumbButton
                        onClick={() => {
                           navigate(item.path)
                        }}
                     >
                        <ChevronLeft24Regular />
                     </BreadcrumbButton>
                  </BreadcrumbItem>
               )}

               <BreadcrumbItem
                  key={`${item.title}-title`}
                  className={mergeClasses(!item.active ? classes.hideInPhone : '', items.length > 1 ? classes.calcWidth : '')}
                  size="large"
               >
                  <BreadcrumbButton
                     className={items.length > 1 ? classes.calcWidth : ''}
                     onClick={() => {
                        if (!item.active) navigate(item.path)
                     }}
                     size="large"
                     current={item.active}
                  >
                     <Title as="h2" noMargin weight={item.active ? 'bold' : 'regular'}>
                        {item.title}
                     </Title>
                  </BreadcrumbButton>
               </BreadcrumbItem>
               {index !== items.length - 1 && (
                  <BreadcrumbDivider key={`${item.title}-divider`} className={classes.hideInPhone} style={{ fontSize: tokens.fontSizeBase500 }} />
               )}
               {index === items.findIndex((x) => x.active) && items.length > 1 && (
                  <BreadcrumbItem key={`${item.title}-item`} className={mergeClasses(classes.showOnlyPhone, classes.width40)} size="large" />
               )}
            </Fragment>
         )
      })
   }
   return (
      <div className={classes.breadCrumbHeader}>
         <Breadcrumb className={mergeClasses(classes.breadCrumbWrapper, breadCrumbItems.length === 1 ? classes.breadCrumbWrapperPadding : '')}>
            {generateBreadcrumbs(breadCrumbItems, navigate)}
         </Breadcrumb>
         {title && (
            <div className={mergeClasses(classes.padding, classes.showOnlyPhone)}>
               <Title as="h3" className={classes.showOnlyPhoneInline}>
                  {title}
               </Title>
               <Divider appearance="brand" />
            </div>
         )}
      </div>
   )
}
